import {
  CmsContentAboutCoreValueSchema,
  type CmsContentAboutCoreValueForm,
} from "@/modules/cms/domain/contracts";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import {
  Button,
  FormCheckbox,
  FormInput,
  FormNumber,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";

const IMAGE_KEY_PREFIX = "uploads/cms/about-core-value";

type AboutCoreValueFormProps = {
  content: CmsContentAboutCoreValueForm | null;
  onSubmit: (content: CmsContentAboutCoreValueForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutCoreValueForm = {
  title: {},
  items: [],
  isActive: true,
};

export function AboutCoreValueForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutCoreValueFormProps) {
  const methods = RHF.useForm<CmsContentAboutCoreValueForm>({
    resolver: zodResolver(
      CmsContentAboutCoreValueSchema,
    ) as Resolver<CmsContentAboutCoreValueForm>,
    defaultValues: content ?? defaultContent,
  });
  const { fields, append, remove } = RHF.useFieldArray({
    control: methods.control,
    name: "items",
  });
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});

  const setPending = useCallback((index: number, file: File | null) => {
    setPendingFiles((prev) => {
      const next = { ...prev };
      if (file) next[index] = file;
      else delete next[index];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (vals: CmsContentAboutCoreValueForm) => {
      const items = (vals.items ?? []).map((item, i) => {
        const f = pendingFiles[i];
        const imageKey = f ? "" : (item.imageKey ?? "");
        return {
          imageKey,
          title: item.title ?? {},
          content: item.content ?? {},
          isActive: item.isActive ?? true,
          order: item.order ?? i,
        };
      });
      for (let i = 0; i < items.length; i++) {
        const f = pendingFiles[i];
        const row = items[i];
        if (f && row) {
          row.imageKey = await uploadImageToKey(IMAGE_KEY_PREFIX, f);
        }
      }
      setPendingFiles({});
      onSubmit({ ...vals, items });
    },
    [onSubmit, pendingFiles],
  );

  return (
    <FormRoot<CmsContentAboutCoreValueForm>
      methods={methods as UseFormReturn<CmsContentAboutCoreValueForm>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormInput name="title.lo" label="ຫົວຂໍ້ (ລາວ)" />
          <FormInput name="title.th" label="ຫົວຂໍ້ (ໄທ)" />
          <FormInput name="title.en" label="ຫົວຂໍ້ (EN)" />
        </div>
        <div className="space-y-2">
          <span className="font-medium text-sm">ລາຍການຄຸນຄ່າຫຼັກ</span>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="rounded border p-4 md:grid md:grid-cols-[minmax(0,180px)_1fr] md:gap-4"
            >
              <div className="mb-3 md:mb-0">
                <ImageKeyUploadField
                  keyPrefix={IMAGE_KEY_PREFIX}
                  label="ຮູບ"
                  aspectHint="ອັດສ່ວນ 1:1"
                  aspectRatio="aspect-square"
                  widthPx={400}
                  heightPx={400}
                  className="max-w-[180px]"
                  value={methods.watch(`items.${index}.imageKey`) ?? ""}
                  onChange={(key) =>
                    methods.setValue(`items.${index}.imageKey`, key, {
                      shouldDirty: true,
                    })
                  }
                  deferUpload
                  pendingFile={pendingFiles[index] ?? null}
                  onFileSelect={(file) => setPending(index, file)}
                  onClear={() => {
                    methods.setValue(`items.${index}.imageKey`, "", {
                      shouldDirty: true,
                    });
                    setPending(index, null);
                  }}
                />
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-end gap-3">
                  <FormNumber
                    name={`items.${index}.order`}
                    label="ລຳດັບ"
                    min={0}
                    className="w-24"
                  />
                  <FormCheckbox
                    name={`items.${index}.isActive`}
                    label="ເປີດໃຊ້ງານ"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => remove(index)}
                    aria-label="ລຶບ"
                    className="shrink-0"
                  >
                    <TrashIcon className="size-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <FormInput
                    name={`items.${index}.title.lo`}
                    label="ຫົວຂໍ້ (ລາວ)"
                  />
                  <FormInput
                    name={`items.${index}.title.th`}
                    label="ຫົວຂໍ້ (ໄທ)"
                  />
                  <FormInput
                    name={`items.${index}.title.en`}
                    label="ຫົວຂໍ້ (EN)"
                  />
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <FormTextarea
                    name={`items.${index}.content.lo`}
                    label="ເນື້ອຫາ (ລາວ)"
                    rows={2}
                  />
                  <FormTextarea
                    name={`items.${index}.content.th`}
                    label="ເນື້ອຫາ (ໄທ)"
                    rows={2}
                  />
                  <FormTextarea
                    name={`items.${index}.content.en`}
                    label="ເນື້ອຫາ (EN)"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({
                imageKey: "",
                title: {},
                content: {},
                isActive: true,
                order: fields.length,
              })
            }
          >
            <PlusIcon className="size-4" />
            ເພີ່ມລາຍການ
          </Button>
        </div>
        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
